import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  FileText,
  Loader2,
  ChevronDown,
  CheckCircle2,
  FileUp,
  Sparkles,
  Info,
  Layers,
  BookOpen,
  Library,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { uploadDocument } from "../../../api/elearning/documents/documents";
import { getSubjectsLookup } from "../../../api/elearning/subjects/subjectInfo";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { startEduDocumentCreateTour } from "./eduDocumentCreateTour";

const documentTypes = [
  { label: "Giáo trình", value: "giao_trinh" },
  { label: "Bài giảng", value: "bai_giang" },
  { label: "Bài tập", value: "bai_tap" },
  { label: "Video bài học", value: "video" },
  { label: "Đề kiểm tra", value: "de_thi" },
  { label: "Khác", value: "khac" },
];

const fileTypeRules = {
  bai_giang: {
    accept: ".zip",
    maxSize: 2 * 1024 * 1024 * 1024,
    message: "Chỉ nhận .zip · tối đa 2GB",
  },
  de_thi: {
    accept: ".pdf",
    maxSize: 50 * 1024 * 1024,
    message: "Chỉ nhận .pdf · tối đa 50MB",
  },
  giao_trinh: {
    accept: ".pdf",
    maxSize: 50 * 1024 * 1024,
    message: "Chỉ nhận .pdf · tối đa 50MB",
  },
  bai_tap: {
    accept: ".pdf",
    maxSize: 50 * 1024 * 1024,
    message: "Chỉ nhận .pdf · tối đa 50MB",
  },
  khac: {
    accept: ".pdf",
    maxSize: 50 * 1024 * 1024,
    message: "Chỉ nhận .pdf · tối đa 50MB",
  },
  video: {
    accept: ".mp4",
    maxSize: 2 * 1024 * 1024 * 1024,
    message: "Chỉ nhận .mp4 · tối đa 2GB",
  },
};

const formatBytesPerSecond = (bps) => {
  if (!Number.isFinite(bps) || bps <= 0) return "0 B/s";
  if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(2)} MB/s`;
  if (bps >= 1024) return `${(bps / 1024).toFixed(2)} KB/s`;
  return `${Math.round(bps)} B/s`;
};

const formatEtaSeconds = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return "";
  const s = Math.ceil(sec);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  if (mm > 0) return `~${mm}p ${String(ss).padStart(2, "0")}s`;
  return `~${ss}s`;
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`;
  return `${bytes} B`;
};

const DROPDOWN_Z = 10060;

function FieldLabel({ children, required, hint }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <label className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
        {children}
        {required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </label>
      {hint ? (
        <span className="text-xs font-medium text-slate-400">{hint}</span>
      ) : null}
    </div>
  );
}

function SectionCard({ icon: Icon, title, description, children, tourId }) {
  return (
    <section
      className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm md:p-6"
      data-tour={tourId}
    >
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <Icon size={20} strokeWidth={2.2} />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm leading-relaxed text-slate-500">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function SummaryRow({ label, value, dark = false, muted = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={dark ? "text-xs text-white/60" : "text-xs text-slate-400"}>
        {label}
      </span>
      <span
        className={
          dark
            ? `max-w-[62%] text-right text-sm font-semibold ${
                muted ? "text-white/45" : "text-white"
              }`
            : `max-w-[62%] text-right text-sm font-semibold ${
                muted ? "text-slate-400" : "text-slate-700"
              }`
        }
      >
        {value}
      </span>
    </div>
  );
}

function StatusChip({ icon: Icon, label, highlighted }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
        highlighted
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-slate-200 bg-white text-slate-600"
      }`}
    >
      {Icon ? <Icon size={13} strokeWidth={2.2} /> : null}
      {label}
    </span>
  );
}

const CustomSelect = ({
  label,
  required,
  hint,
  value,
  onChange,
  options,
  placeholder,
  loading,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  const syncMenuPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const desiredMax = 260;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
    const maxListH = Math.min(
      desiredMax,
      openUp ? spaceAbove - 12 : spaceBelow - 12
    );
    const left = Math.max(
      margin,
      Math.min(r.left, window.innerWidth - r.width - margin)
    );
    setMenuRect({
      left,
      width: r.width,
      maxHeight: Math.max(100, maxListH),
      ...(openUp
        ? { bottom: window.innerHeight - r.top + margin }
        : { top: r.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuRect(null);
      return;
    }
    syncMenuPosition();
    const onScroll = () => syncMenuPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [isOpen, options.length, syncMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointer = (e) => {
      const t = triggerRef.current;
      const m = menuRef.current;
      if (t?.contains(e.target) || m?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  const menuPortal =
    isOpen &&
    menuRect &&
    typeof document !== "undefined" &&
    createPortal(
      <motion.div
        ref={menuRef}
        role="listbox"
        initial={{ opacity: 0, y: menuRect.top != null ? -4 : 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1 }}
        className="fixed overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5"
        style={{
          left: menuRect.left,
          width: menuRect.width,
          zIndex: DROPDOWN_Z,
          ...(menuRect.top != null ? { top: menuRect.top } : {}),
          ...(menuRect.bottom != null ? { bottom: menuRect.bottom } : {}),
          maxHeight: menuRect.maxHeight,
        }}
      >
        <div
          className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
          style={{ maxHeight: menuRect.maxHeight }}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2.5 text-center text-sm text-slate-400">
              Không có dữ liệu
            </div>
          ) : (
            options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                  value === opt.value
                    ? "bg-red-50 font-semibold text-red-700"
                    : "font-medium text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {value === opt.value ? (
                  <CheckCircle2 size={15} className="shrink-0 text-red-600" />
                ) : null}
              </button>
            ))
          )}
        </div>
      </motion.div>,
      document.body
    );

  return (
    <div>
      {label ? (
        <FieldLabel required={required} hint={hint}>
          {label}
        </FieldLabel>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`flex h-11 w-full items-center justify-between gap-2 rounded-2xl border bg-white px-4 text-left text-sm transition-colors ${
          isOpen
            ? "border-red-400 ring-2 ring-red-50"
            : "border-slate-200 hover:border-slate-300"
        } ${
          disabled || loading
            ? "cursor-not-allowed bg-slate-50 opacity-70"
            : "cursor-pointer"
        }`}
      >
        <span
          className={`min-w-0 truncate font-medium ${
            !selectedOption ? "text-slate-400" : "text-slate-800"
          }`}
        >
          {loading
            ? "Đang tải…"
            : selectedOption
              ? selectedOption.label
              : placeholder}
        </span>
        {loading ? (
          <Loader2 size={16} className="shrink-0 animate-spin text-slate-400" />
        ) : (
          <ChevronDown
            size={16}
            className={`shrink-0 text-slate-400 transition-transform ${
              isOpen ? "rotate-180 text-red-600" : ""
            }`}
          />
        )}
      </button>
      {menuPortal}
    </div>
  );
};

const DocumentCreateModal = ({ isOpen, onClose, onSuccess, onStartUploads }) => {
  const [loading, setLoading] = useState(false);
  const startingUploadsRef = React.useRef(false);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [uploadUi, setUploadUi] = useState({
    percent: 0,
    phase: "upload",
    speedText: "",
    etaText: "",
    elapsedMs: 0,
  });
  const [uploadWarning, setUploadWarning] = useState("");

  const startedAtRef = React.useRef(null);
  const lastSampleRef = React.useRef({ loaded: 0, t: 0 });
  const lastUiUpdateRef = React.useRef(0);
  const tickRef = React.useRef(null);
  const percentRef = React.useRef(0);
  const phaseRef = React.useRef("upload");

  const [formData, setFormData] = useState({
    subject: "",
    courseId: "",
    docType: "",
    fileData: null,
    fileList: [],
    fileName: "",
    description: "",
    status: "1",
  });

  const resetForm = () => {
    setFormData({
      subject: "",
      courseId: "",
      docType: "",
      fileData: null,
      fileList: [],
      fileName: "",
      description: "",
      status: "1",
    });
    setLoading(false);
    startingUploadsRef.current = false;
    setUploadUi({
      percent: 0,
      phase: "upload",
      speedText: "",
      etaText: "",
      elapsedMs: 0,
    });
    setUploadWarning("");
  };

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    setSubjectsLoading(true);
    getSubjectsLookup()
      .then((res) => {
        if (!isMounted) return;
        setSubjects(Array.isArray(res) ? res : res?.items || []);
      })
      .catch((err) => {
        console.error("Lỗi khi lấy danh sách môn học:", err);
        showErrorToast(
          err.response?.data?.message || err.message || "Không thể tải môn học."
        );
        if (!isMounted) return;
        setSubjects([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setSubjectsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !formData.subject) {
      setCourses([]);
      return;
    }
    const sub = subjects.find((s) => s.code === formData.subject);
    if (!sub?.id) {
      setCourses([]);
      return;
    }
    let cancelled = false;
    setCoursesLoading(true);
    getAllCoursesNoPaging({ subject_id: sub.id, is_active: "true" })
      .then((data) => {
        if (cancelled) return;
        const raw = Array.isArray(data)
          ? data
          : (data?.records ?? data?.items ?? []);
        setCourses(Array.isArray(raw) ? raw : []);
      })
      .catch((err) => {
        console.error("Lỗi khi lấy khóa học:", err);
        if (!cancelled) setCourses([]);
      })
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.subject, subjects]);

  const handleFileChange = (e) => {
    if (loading) return;
    const inputFiles = Array.from(e.target.files || []);
    if (!inputFiles.length || !formData.docType) return;

    if (inputFiles.length > 10) {
      showErrorToast("Tối đa 10 tệp/lần chọn.");
      return;
    }

    const rule = fileTypeRules[formData.docType];
    const fileInfos = [];

    for (const file of inputFiles) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!rule.accept.includes(`.${ext}`)) return showErrorToast(rule.message);
      if (file.size > rule.maxSize) return showErrorToast(rule.message);
      const baseName = file.name.replace(/\.[^/.]+$/, "");
      fileInfos.push({
        file,
        size: file.size,
        type: file.type,
        ext,
        baseName,
      });
    }

    if (fileInfos.length === 1) {
      setFormData((prev) => ({
        ...prev,
        fileData: {
          file: fileInfos[0].file,
          size: fileInfos[0].size,
          type: fileInfos[0].type,
          ext: fileInfos[0].ext,
        },
        fileList: [],
        fileName: fileInfos[0].baseName,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        fileData: null,
        fileList: fileInfos,
        fileName: "",
      }));
    }
  };

  const clearFiles = () => {
    if (loading) return;
    setFormData((prev) => ({
      ...prev,
      fileData: null,
      fileList: [],
      fileName: "",
    }));
  };

  const handleSave = async () => {
    if (loading) return;
    if (startingUploadsRef.current) return;

    if (!formData.subject || !formData.docType) {
      return showErrorToast("Vui lòng chọn môn học và phân loại tài liệu");
    }
    if (
      !formData.fileData &&
      (!formData.fileList || formData.fileList.length === 0)
    ) {
      return showErrorToast("Vui lòng chọn tệp tài liệu");
    }

    if (onStartUploads) {
      let filesToUpload = [];
      if (Array.isArray(formData.fileList) && formData.fileList.length > 0) {
        filesToUpload = formData.fileList;
      } else if (formData.fileData) {
        if (!formData.fileName?.trim()) {
          return showErrorToast("Vui lòng nhập tên tài liệu");
        }
        filesToUpload = [
          {
            file: formData.fileData.file,
            size: formData.fileData.size,
            type: formData.fileData.type,
            ext: formData.fileData.ext,
            baseName: formData.fileName.trim(),
          },
        ];
      }

      if (!filesToUpload.length) {
        return showErrorToast("Vui lòng chọn tệp tài liệu");
      }

      startingUploadsRef.current = true;
      try {
        onStartUploads({
          files: filesToUpload,
          meta: {
            subject: formData.subject,
            courseId: formData.courseId,
            docType: formData.docType,
            description: formData.description,
            status: formData.status,
          },
        });
        showSuccessToast(
          `Đã thêm ${filesToUpload.length} tệp vào tiến trình tải lên.`
        );
        resetForm();
        onClose?.();
      } finally {
        startingUploadsRef.current = false;
      }
      return;
    }

    if (!formData.fileName) return showErrorToast("Vui lòng nhập tên tài liệu");

    setLoading(true);
    startedAtRef.current = Date.now();
    lastSampleRef.current = { loaded: 0, t: startedAtRef.current };
    lastUiUpdateRef.current = 0;
    percentRef.current = 0;
    phaseRef.current = "upload";
    setUploadWarning("");
    setUploadUi({
      percent: 0,
      phase: "upload",
      speedText: "",
      etaText: "",
      elapsedMs: 0,
    });

    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      if (!startedAtRef.current) return;
      const elapsedMs = Date.now() - startedAtRef.current;
      setUploadUi((prev) => ({ ...prev, elapsedMs }));
      if (percentRef.current >= 99 && elapsedMs >= 60_000) {
        setUploadWarning("Server đang xử lý lâu hơn dự kiến. Vui lòng chờ…");
      }
    }, 500);

    const f = formData.fileData.file;
    const data = new FormData();
    data.append("file", f);
    data.append("file_name", formData.fileName);
    data.append("file_type", f.type || "application/octet-stream");
    data.append("file_extension", formData.fileData.ext || "");
    data.append("file_size", String(f.size));
    data.append("course_id", formData.courseId || "");
    data.append("type", formData.docType);
    data.append("subject", formData.subject);
    data.append("description", formData.description);
    data.append("is_active", formData.status === "1" ? "true" : "false");

    try {
      await uploadDocument(data, {
        onProgress: ({ loaded, total }) => {
          const now = Date.now();
          const startedAt = startedAtRef.current ?? now;
          const prev = lastSampleRef.current;
          const dt = now - prev.t;
          const dLoaded = loaded - prev.loaded;
          const hasTotal = total && total > 0;
          const nextPercent = hasTotal
            ? Math.min(100, Math.round((loaded / total) * 100))
            : percentRef.current;
          percentRef.current = nextPercent;

          let bps = 0;
          if (dt > 0 && dLoaded > 0) bps = (dLoaded / dt) * 1000;
          const speedText = bps ? formatBytesPerSecond(bps) : "";
          const remainingSec =
            hasTotal && bps > 0 && nextPercent < 100
              ? (total - loaded) / bps
              : null;
          const etaText = remainingSec ? formatEtaSeconds(remainingSec) : "";
          const nextPhase = nextPercent >= 100 ? "server" : "upload";
          phaseRef.current = nextPhase;
          const nextElapsedMs = Date.now() - startedAt;

          lastSampleRef.current = { loaded, t: now };
          if (
            now - (lastUiUpdateRef.current || 0) < 200 &&
            nextPercent !== 100
          )
            return;
          lastUiUpdateRef.current = now;

          setUploadUi({
            percent: nextPercent,
            phase: nextPhase,
            speedText,
            etaText,
            elapsedMs: nextElapsedMs,
          });

          if (nextPhase === "server") setUploadWarning("");
          if (nextPhase === "upload" && bps > 0 && bps < 200 * 1024) {
            setUploadWarning(
              "Tốc độ upload đang chậm. Có thể do mạng/thiết bị yếu."
            );
          }
          if (nextPhase === "upload" && bps >= 200 * 1024) {
            setUploadWarning("");
          }
        },
      });

      showSuccessToast("Tải lên tài liệu thành công!");
      resetForm();
      onSuccess?.();
      onClose();
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || err.message || "Không thể lưu tài liệu."
      );
    } finally {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      startedAtRef.current = null;
      setLoading(false);
    }
  };

  const hasFiles =
    Boolean(formData.fileData) ||
    (Array.isArray(formData.fileList) && formData.fileList.length > 0);
  const multiFiles =
    Array.isArray(formData.fileList) && formData.fileList.length > 1;
  const fileRule = formData.docType ? fileTypeRules[formData.docType] : null;

  const subjectLabel = useMemo(() => {
    const s = subjects.find((x) => x.code === formData.subject);
    return s?.name || "";
  }, [subjects, formData.subject]);

  const courseLabel = useMemo(() => {
    if (!formData.courseId) return "";
    const c = courses.find((x) => String(x.id) === String(formData.courseId));
    return c?.name || "";
  }, [courses, formData.courseId]);

  const typeLabel =
    documentTypes.find((t) => t.value === formData.docType)?.label || "";

  const displayName = multiFiles
    ? `${formData.fileList.length} tài liệu (theo tên file)`
    : formData.fileName || formData.fileData?.file?.name || "";

  const fileSummary = multiFiles
    ? formData.fileList.map((f) => f.file.name).join(", ")
    : formData.fileData?.file?.name || "";

  const completionCount = [
    Boolean(formData.subject),
    Boolean(formData.docType),
    hasFiles,
    multiFiles || Boolean(String(formData.fileName || "").trim()),
  ].filter(Boolean).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-slate-900/55 backdrop-blur-md"
            onClick={() => {
              if (!loading) onClose?.();
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4"
          >
            <div
              className="pointer-events-auto flex max-h-[min(92vh,860px)] w-full max-w-5xl flex-col overflow-hidden rounded-[1.75rem] border border-slate-100 bg-white shadow-2xl"
              data-tour="edu-doc-create-modal"
            >
              {/* Header */}
              <div
                className="shrink-0 border-b border-slate-100 bg-gradient-to-r from-red-50 via-white to-white px-6 pb-5 pt-6 md:px-8"
                data-tour="edu-doc-create-header"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg shadow-red-200">
                      <FileText size={20} />
                    </span>
                    <div className="min-w-0">
                      <h2 className="text-xl font-black tracking-tight text-slate-800">
                        Thêm tài liệu học tập
                      </h2>
                      <p className="mt-1 text-sm text-slate-500">
                        Nhập cấu hình bên trái — cột phải cập nhật kết quả khởi
                        tạo theo thời gian thực.
                      </p>
                    </div>
                  </div>
                  <div
                    className="flex shrink-0 items-center gap-1"
                    data-tour="edu-doc-create-guide-btn"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        window.setTimeout(
                          () => startEduDocumentCreateTour(),
                          80
                        )
                      }
                      title="Hướng dẫn nhanh"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                    >
                      <Sparkles size={16} strokeWidth={2.2} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!loading) onClose?.();
                      }}
                      disabled={loading}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <StatusChip
                    icon={BookOpen}
                    label={subjectLabel || "Chưa chọn môn"}
                  />
                  <StatusChip
                    icon={Layers}
                    label={typeLabel || "Chưa chọn loại"}
                  />
                  <StatusChip
                    icon={CheckCircle2}
                    label={`Hoàn thiện ${completionCount}/4 mục`}
                    highlighted
                  />
                </div>
              </div>

              {/* Body 2 columns */}
              <div
                className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-6 py-6 md:px-8"
                data-tour="edu-doc-create-form"
              >
                <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1.55fr)_340px]">
                  {/* LEFT */}
                  <div className="space-y-6">
                    <SectionCard
                      icon={BookOpen}
                      title="Phân loại"
                      description="Môn học và loại tài liệu bắt buộc. Khóa học để trống nếu dùng chung."
                      tourId="edu-doc-create-config"
                    >
                      <div className="space-y-5">
                        <CustomSelect
                          label="Môn học"
                          required
                          placeholder="Chọn môn học"
                          loading={subjectsLoading}
                          disabled={loading}
                          value={formData.subject}
                          options={subjects.map((s) => ({
                            label: s.name,
                            value: s.code,
                          }))}
                          onChange={(val) =>
                            setFormData({
                              ...formData,
                              subject: val,
                              courseId: "",
                              fileData: null,
                              fileList: [],
                              fileName: "",
                            })
                          }
                        />

                        <div>
                          <FieldLabel required>Phân loại</FieldLabel>
                          <div className="flex flex-wrap gap-2">
                            {documentTypes.map((t) => {
                              const active = formData.docType === t.value;
                              return (
                                <button
                                  key={t.value}
                                  type="button"
                                  disabled={loading}
                                  onClick={() =>
                                    setFormData({
                                      ...formData,
                                      docType: t.value,
                                      fileData: null,
                                      fileList: [],
                                      fileName: "",
                                    })
                                  }
                                  className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition ${
                                    active
                                      ? "border-red-600 bg-red-600 text-white"
                                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                                  } disabled:opacity-50`}
                                >
                                  {t.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <CustomSelect
                          label="Khóa học"
                          hint="Không bắt buộc"
                          placeholder={
                            formData.subject
                              ? "Dùng chung môn học"
                              : "Chọn môn học trước"
                          }
                          loading={coursesLoading}
                          disabled={loading || !formData.subject}
                          value={formData.courseId}
                          options={[
                            { label: "Dùng chung môn học", value: "" },
                            ...courses.map((c) => ({
                              label: c.name,
                              value: c.id,
                            })),
                          ]}
                          onChange={(val) =>
                            setFormData({ ...formData, courseId: val || "" })
                          }
                        />
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={FileUp}
                      title="Tệp tài liệu"
                      description="Chọn phân loại trước. Có thể tải tối đa 10 tệp một lần."
                      tourId="edu-doc-create-file"
                    >
                      <div
                        className={`relative rounded-3xl border-2 border-dashed px-5 py-7 transition ${
                          hasFiles
                            ? "border-red-200 bg-red-50/40"
                            : "border-slate-200 bg-slate-50/70 hover:border-red-300 hover:bg-red-50/20"
                        } ${!formData.docType || loading ? "opacity-60" : ""}`}
                      >
                        <input
                          type="file"
                          multiple
                          accept={fileRule?.accept || "*"}
                          className="absolute inset-0 z-10 cursor-pointer opacity-0 disabled:cursor-not-allowed"
                          onChange={handleFileChange}
                          disabled={loading || !formData.docType}
                        />
                        <div className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                              hasFiles
                                ? "bg-white text-red-600 shadow-sm"
                                : "bg-white text-slate-400"
                            }`}
                          >
                            {hasFiles ? (
                              <CheckCircle2 size={20} />
                            ) : (
                              <FileUp size={20} />
                            )}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-800">
                              {multiFiles
                                ? `${formData.fileList[0].file.name} (+${formData.fileList.length - 1})`
                                : formData.fileData
                                  ? formData.fileData.file.name
                                  : formData.docType
                                    ? "Chọn hoặc kéo thả tệp"
                                    : "Chọn phân loại trước khi tải tệp"}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              {fileRule
                                ? fileRule.message
                                : "Hỗ trợ tối đa 10 tệp / lần"}
                              {formData.fileData
                                ? ` · ${formatFileSize(formData.fileData.size)}`
                                : multiFiles
                                  ? ` · ${formData.fileList.length} tệp`
                                  : ""}
                            </p>
                          </div>
                          {hasFiles ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                clearFiles();
                              }}
                              className="relative z-20 shrink-0 rounded-xl px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-white hover:text-red-600"
                            >
                              Đổi tệp
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={Info}
                      title="Thông tin hiển thị"
                      description="Tên và mô tả xuất hiện trên thẻ tài liệu trong thư viện."
                      tourId="edu-doc-create-meta"
                    >
                      <div className="space-y-5">
                        <div>
                          <FieldLabel required={!multiFiles}>
                            Tên hiển thị
                          </FieldLabel>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-red-400 focus:ring-2 focus:ring-red-50 disabled:bg-slate-50"
                            value={formData.fileName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                fileName: e.target.value,
                              })
                            }
                            placeholder={
                              multiFiles
                                ? "Tự lấy theo tên từng file"
                                : "Nhập tên tài liệu"
                            }
                            disabled={loading || multiFiles}
                          />
                        </div>
                        <div>
                          <FieldLabel>Mô tả</FieldLabel>
                          <textarea
                            rows={3}
                            className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-red-400 focus:ring-2 focus:ring-red-50 disabled:bg-slate-50"
                            value={formData.description}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                description: e.target.value,
                              })
                            }
                            placeholder="Ghi chú ngắn (không bắt buộc)"
                            disabled={loading}
                          />
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                          <input
                            type="checkbox"
                            checked={formData.status === "1"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                status: e.target.checked ? "1" : "0",
                              })
                            }
                            disabled={loading}
                            className="rounded border-slate-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="font-medium">
                            Đang sử dụng (hiển thị khi gán bài học)
                          </span>
                        </label>
                      </div>
                    </SectionCard>
                  </div>

                  {/* RIGHT — kết quả khởi tạo */}
                  <div
                    className="space-y-5 xl:sticky xl:top-0"
                    data-tour="edu-doc-create-preview"
                  >
                    <div className="rounded-[1.75rem] border border-slate-200 bg-slate-900 p-5 text-white shadow-xl shadow-slate-900/10">
                      <div className="mb-4 flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                          <FileText size={20} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                            Kết quả khởi tạo
                          </p>
                          <p className="truncate text-base font-bold">
                            {displayName || "Chưa có tên tài liệu"}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <SummaryRow
                          label="Môn học"
                          value={subjectLabel || "Chưa chọn"}
                          dark
                          muted={!subjectLabel}
                        />
                        <SummaryRow
                          label="Phân loại"
                          value={typeLabel || "Chưa chọn"}
                          dark
                          muted={!typeLabel}
                        />
                        <SummaryRow
                          label="Khóa học"
                          value={
                            formData.subject
                              ? courseLabel || "Dùng chung môn học"
                              : "Chưa chọn môn"
                          }
                          dark
                          muted={!formData.subject}
                        />
                        <SummaryRow
                          label="Trạng thái"
                          value={
                            formData.status === "1"
                              ? "Đang sử dụng"
                              : "Tạm ngưng"
                          }
                          dark
                        />
                      </div>
                    </div>

                    <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm">
                      <div className="mb-4 flex items-center gap-2">
                        <Library size={18} className="text-red-500" />
                        <h3 className="font-bold text-slate-800">
                          Tóm tắt tài liệu
                        </h3>
                      </div>
                      <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-4">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-red-400">
                          {multiFiles ? "Số tài liệu sẽ tạo" : "Tệp đính kèm"}
                        </p>
                        <p className="mt-1 text-2xl font-black text-red-700">
                          {multiFiles
                            ? `${formData.fileList.length} tệp`
                            : hasFiles
                              ? formatFileSize(formData.fileData?.size) ||
                                "1 tệp"
                              : "Chưa chọn tệp"}
                        </p>
                        <p className="mt-2 line-clamp-2 text-xs italic text-red-500">
                          {fileSummary ||
                            "Chọn tệp ở cột trái để xem kết quả tại đây"}
                        </p>
                      </div>
                      <div className="space-y-3">
                        <SummaryRow
                          label="Hoàn thiện"
                          value={`${completionCount}/4 mục chính`}
                          muted={completionCount < 4}
                        />
                        <SummaryRow
                          label="Định dạng"
                          value={
                            fileRule?.accept?.replace(".", "").toUpperCase() ||
                            "—"
                          }
                          muted={!fileRule}
                        />
                        <SummaryRow
                          label="Mô tả"
                          value={
                            formData.description?.trim()
                              ? formData.description.trim()
                              : "Không có"
                          }
                          muted={!formData.description?.trim()}
                        />
                      </div>
                    </div>

                    <div className="rounded-[1.75rem] border border-amber-100 bg-amber-50 p-5">
                      <div className="flex items-start gap-3">
                        <Info
                          size={18}
                          className="mt-0.5 shrink-0 text-amber-600"
                        />
                        <div>
                          <p className="text-sm font-bold text-amber-800">
                            Lưu ý trước khi lưu
                          </p>
                          <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-amber-700">
                            <li>Môn học + phân loại + tệp là bắt buộc.</li>
                            <li>
                              Để trống khóa học nếu tài liệu dùng chung nhiều
                              khóa cùng môn.
                            </li>
                            <li>
                              Nhiều tệp: mỗi file tạo một tài liệu, tên lấy theo
                              tên file.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="shrink-0 border-t border-slate-100 bg-slate-50/80 px-6 py-5 md:px-8"
                data-tour="edu-doc-create-actions"
              >
                {loading ? (
                  <div className="mb-4">
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full bg-red-600 transition-all"
                        style={{ width: `${uploadUi.percent}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-4">
                      <p className="text-xs font-semibold text-slate-600">
                        {uploadUi.phase === "upload"
                          ? "Đang tải lên…"
                          : "Đang xử lý server…"}
                        {uploadUi.speedText ? (
                          <span className="ml-2 font-medium text-slate-400">
                            {uploadUi.speedText}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs font-semibold text-slate-400">
                        {uploadUi.etaText
                          ? `Còn ${uploadUi.etaText}`
                          : uploadUi.elapsedMs
                            ? `~${Math.ceil(uploadUi.elapsedMs / 1000)}s`
                            : null}
                      </p>
                    </div>
                    {uploadWarning ? (
                      <p className="mt-2 text-xs font-semibold text-red-600">
                        {uploadWarning}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (!loading) onClose?.();
                    }}
                    disabled={loading}
                    className="rounded-2xl px-6 py-3 text-sm font-bold text-slate-500 transition hover:text-red-600 disabled:opacity-50"
                  >
                    Hủy bỏ
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-red-200 transition hover:bg-red-700 active:scale-[0.98] disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <CheckCircle2 size={18} />
                    )}
                    {loading ? "Đang lưu…" : "Lưu tài liệu"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DocumentCreateModal;
